import { endpoint as ep } from './http'
import type { Catalog } from '../types/catalog'
import type {
  BusinessProfileReport,
  CustomerInsights,
  InsightsOverview,
  IntegrationId,
  Integrations,
  IntegrationStatus,
  MailInsights,
  SalonInsights,
  SearchReport,
  SocialReport,
  SourceInsights,
  VisitorsReport,
  WebshopInsights,
} from '../types/insights'
import type { SiteSettings } from '../types/site'
import type { PageContent } from '../types/pages'
import type { Account, Appointment, Course, Customer, DiaryEntry, SkinPlan, Subscription } from '../types/account'
import type {
  AccountDeleteRequest,
  AccountExport,
  AppointmentRequest,
  BookingRequest,
  BookingResponse,
  CustomerPatch,
  DiaryUploadRequest,
  EmailRequest,
  EmailVerifyRequest,
  LoginRequest,
  MagicLinkVerifyRequest,
  NewsletterRequest,
  OkResponse,
  PasswordChangeRequest,
  PasswordResetConfirmRequest,
  OrderRequest,
  OrderResponse,
  OrderSourceRequest,
  OrderStatusResponse,
  NewsletterConfirmRequest,
  QuizLeadRequest,
  RegisterRequest,
  RescheduleRequest,
  RetryPaymentResponse,
  SubscriptionActionRequest,
  SubscriptionActionResponse,
  SubscriptionUndoRequest,
} from '../types/requests'
import type {
  AdminContent,
  StockUpdateRequest,
  StockUpdateResponse,
  AdminLoginRequest,
  AdminLoginResponse,
  AdminUser,
  CompleteResponse,
  RecoveryCodesResponse,
  TwoFactorChallengeRequest,
  TwoFactorConfirmRequest,
  TwoFactorConfirmResponse,
  TwoFactorSetup,
  CustomerFile,
  CustomerCreateRequest,
  CustomerMergeRequest,
  CustomerContactRequest,
  CustomerSummary,
  DiaryMarkRequest,
  StaffCustomerCard,
  StaffInviteRequest,
  StaffLogin,
  Inbox,
  InboxBooking,
  InboxOrder,
  NewAppointmentRequest,
  NoteRequest,
  BookingBlockRequest,
  BookingBlockResponse,
  OrderStatusRequest,
  ProposeRequest,
  AdminOrder,
  AdminOrderPage,
  AdminOrderQuery,
  CancelOrderRequest,
  RefundRequest,
  SalesChannel,
  SalesGroupBy,
  SalesReport,
} from '../types/adminRequests'
import type {
  AgendaAppointment,
  AgendaBlock,
  AgendaBlockRequest,
  AgendaCompleteRequest,
  AgendaFeed,
  AgendaMoveRequest,
  AgendaAbsenceRequest,
  AgendaAbsenceResult,
  AgendaNewRequest,
  AgendaSeriesRequest,
  AgendaSeriesResult,
  AgendaRange,
  AvailabilityDay,
} from '../types/agenda'
import type {
  CreditCorrectionRequest,
  LiabilitiesReport,
  VoucherBlockRequest,
  VoucherCorrectionRequest,
  CustomerCredit,
  NewVoucherRequest,
  Voucher,
  VoucherCheckRequest,
  VoucherCheckResponse,
  VoucherPage,
  VoucherQuery,
} from '../types/vouchers'
import type { RedirectsRequest, RedirectsResponse } from '../types/seo'
import type {
  MailSettings,
  MailTestRequest,
  NewsletterDeliveryResponse,
  NewsletterScheduleRequest,
  NewsletterSendRequest,
  SegmentList,
  SegmentsRequest,
  SubscriberPage,
  SubscriberQuery,
} from '../types/mail'
import type { CartSyncRequest } from '../types/requests'
import type { CashDayClose, CloseDayRequest, KassaDay, KuurStop, KuurStopRequest, Sale, SaleRequest, SaleReturn, SaleReturnRequest, VoidSaleRequest } from '../types/kassa'
import type { HandoffNote, HandoffNoteRequest } from '../types/handoff'
import type { AuditPage, AuditQuery, ReauthRequest, StaffAccessRequest } from '../types/team'
import type {
  NewStockItemRequest,
  PurchaseReceipt,
  PurchaseReceiptRequest,
  StockCount,
  StockCountRequest,
  StockCountResult,
  StockItem,
  StockItemPatch,
  StockMovement,
  StockMovementRequest,
} from '../types/stock'
import type { StockOverview } from '../types/backend'
import { parseOrderResponse, parseOrderStatus, parseRetryPayment, parseTwoFactorSetup } from './guards'

/**
 * HET CONTRACT MET DE BACKEND — elk eindpunt op één plek.
 *
 * `ep<Verzoek, Antwoord>()` koppelt de typen aan het eindpunt. `call()` in http.ts leidt
 * body- en antwoordtype hieruit af: een verkeerde body of een verkeerd gekoppeld eindpunt
 * is een compileerfout, niet iets wat pas in productie opvalt.
 *
 * Algemene regels:
 * - Sessies via httpOnly-cookies: één voor klanten, één voor het beheer.
 * - Elke schrijfactie met een `requestId` is idempotent: dezelfde id = hetzelfde antwoord.
 * - Fouten: `{ message, fields?, code? }`, met `fields['line:<productId>']` voor één bestelregel.
 * - Alles onder /admin alleen voor de rol admin; de server controleert dat zelf.
 * - `void` als antwoord = 204 zonder body.
 */

const enc = encodeURIComponent
const customer = (id: string) => `/admin/customers/${enc(id)}`
const appointment = (id: string, aid: string) => `${customer(id)}/appointments/${enc(aid)}`
const agendaItem = (id: string) => `/admin/agenda/appointments/${enc(id)}`

export const endpoints = {
  /** Webshop — publiek. */
  shop: {
    catalog: ep<void, Catalog>()({ method: 'GET', path: () => '/catalog' }),
    site: ep<void, SiteSettings>()({ method: 'GET', path: () => '/site' }),
    pages: ep<void, PageContent>()({ method: 'GET', path: () => '/pages' }),
    /**
     * 409 `{ code: 'total_mismatch', message }` als `expectedTotalCents` niet klopt met wat de
     * server rekent. Het antwoord wordt gecontroleerd: `paymentUrl` moet https zijn.
     */
    order: ep<OrderRequest, OrderResponse>()({ method: 'POST', path: () => '/orders', parse: parseOrderResponse }),
    /**
     * Terugkeerpagina na Mollie. `t` = het ondertekende token uit de redirectUrl
     * (`/bestelling/{id}?t=…`): zo werkt de pagina ook als de bank-app terugkeert in een andere
     * browser dan waarin besteld is. Geldig zolang de order openstaat + 1 uur; zonder geldig
     * token én zonder de sessie van de order: 404. Geeft alleen status en bedragen, geen persoonsgegevens.
     */
    orderStatus: ep<void, OrderStatusResponse>()({
      method: 'GET',
      path: (id: string, token: string) => `/orders/${enc(id)}/status${token ? `?t=${enc(token)}` : ''}`,
      parse: parseOrderStatus,
    }),
    /** Opnieuw betalen na mislukt/verlopen. 409 `already_paid` als hij intussen toch betaald is. Zelfde token-regel. */
    retryPayment: ep<void, RetryPaymentResponse>()({
      method: 'POST',
      path: (id: string, token: string) => `/orders/${enc(id)}/pay${token ? `?t=${enc(token)}` : ''}`,
      parse: parseRetryPayment,
    }),
    /**
     * "Hoe kwam je bij ons?" op de bedankpagina (docs/INZICHT.md §5.6). Zelfde token-regel als de
     * status; alleen een betaalde order; één keer per order (een tweede keer: stil `ok`).
     */
    orderSource: ep<OrderSourceRequest, OkResponse>()({
      method: 'POST',
      path: (id: string, token: string) => `/orders/${enc(id)}/source${token ? `?t=${enc(token)}` : ''}`,
    }),
    /** 409 `slot_taken` als de tijd intussen bezet is (de server controleert met een lock). */
    booking: ep<BookingRequest, BookingResponse>()({ method: 'POST', path: () => '/bookings' }),
    /**
     * Vrije starttijden voor één behandeling, de komende salondagen waarop iemand werkt die de
     * behandeling doet; per dag `staffId` (wie het doet). Alleen tijden, nooit wie er komt. `ignore`
     * telt alleen als die afspraak bij de ingelogde klant hoort (verzetten). `staff` = alleen de dagen
     * van die persoon ("Alleen bij Sarah").
     */
    availability: ep<void, AvailabilityDay[]>()({
      method: 'GET',
      path: (treatment: string, ignore: string, staff: string) =>
        `/availability?treatment=${enc(treatment)}${ignore ? `&ignore=${enc(ignore)}` : ''}${staff ? `&staff=${enc(staff)}` : ''}`,
    }),
    quizLead: ep<QuizLeadRequest, OkResponse>()({ method: 'POST', path: () => '/leads/quiz' }),
    newsletter: ep<NewsletterRequest, OkResponse>()({ method: 'POST', path: () => '/newsletter' }),
    /** Dubbele opt-in, stap 2. 422 `link_expired` bij een onbekend of verlopen token. */
    newsletterConfirm: ep<NewsletterConfirmRequest, OkResponse>()({ method: 'POST', path: () => '/newsletter/confirm' }),
    /**
     * Saldo van een cadeaubon bij afrekenen. 422 `voucher_unknown` | `voucher_expired` | `voucher_empty`
     * | `voucher_treatments_only`. Strenge rate limit (raden van codes); POST, zodat de code nooit in een URL of log staat.
     */
    voucherCheck: ep<VoucherCheckRequest, VoucherCheckResponse>()({ method: 'POST', path: () => '/vouchers/check' }),
  },

  /** Mijn omgeving — klantsessie. */
  account: {
    /** 401 = niet ingelogd. */
    me: ep<void, Account>()({ method: 'GET', path: () => '/account' }),
    login: ep<LoginRequest, Account>()({ method: 'POST', path: () => '/auth/login' }),
    register: ep<RegisterRequest, Account>()({ method: 'POST', path: () => '/auth/register' }),
    magicLink: ep<EmailRequest, OkResponse>()({ method: 'POST', path: () => '/auth/magic-link' }),
    magicLinkVerify: ep<MagicLinkVerifyRequest, Account>()({ method: 'POST', path: () => '/auth/magic-link/verify' }),
    passwordReset: ep<EmailRequest, OkResponse>()({ method: 'POST', path: () => '/auth/password-reset' }),
    /** Nieuw wachtwoord via de link uit de mail. 422 `fields.password`; 422 `link_expired`. */
    passwordResetConfirm: ep<PasswordResetConfirmRequest, Account>()({ method: 'POST', path: () => '/auth/password-reset/confirm' }),
    logout: ep<void, void>()({ method: 'POST', path: () => '/auth/logout' }),
    /** 422 = verlopen of al gebruikt. */
    verifyEmail: ep<EmailVerifyRequest, OkResponse>()({ method: 'POST', path: () => '/auth/email/verify' }),
    /** 429 = net al gestuurd. */
    resendVerification: ep<void, OkResponse>()({ method: 'POST', path: () => '/auth/email/verification-notification' }),
    /** Wijzigingstermijn voorbij → 409 `change_window_closed`. Geeft een `undoToken` terug. */
    subscriptionAction: ep<SubscriptionActionRequest, SubscriptionActionResponse>()({
      method: 'POST',
      path: () => '/account/subscription/actions',
    }),
    /** 409 `undo_expired`: verlopen, al gebruikt, of het abonnement veranderde sindsdien. */
    subscriptionUndo: ep<SubscriptionUndoRequest, SubscriptionActionResponse>()({
      method: 'POST',
      path: () => '/account/subscription/undo',
    }),
    requestAppointment: ep<AppointmentRequest, Appointment>()({ method: 'POST', path: () => '/account/appointments' }),
    rescheduleAppointment: ep<RescheduleRequest, Appointment>()({
      method: 'POST',
      path: (id: string) => `/account/appointments/${enc(id)}/reschedule`,
    }),
    cancelAppointment: ep<void, void>()({ method: 'DELETE', path: (id: string) => `/account/appointments/${enc(id)}` }),
    /** Multipart (de body bevat een Blob). 422 `photo_consent_required` zonder actuele toestemming. */
    diaryUpload: ep<DiaryUploadRequest, DiaryEntry>()({ method: 'POST', path: () => '/account/diary' }),
    /** Toestemming intrekken: alle foto's weg. */
    diaryDeleteAll: ep<void, void>()({ method: 'DELETE', path: () => '/account/diary' }),
    updateCustomer: ep<CustomerPatch, Customer>()({ method: 'PATCH', path: () => '/account/customer' }),
    /** 422 `fields.currentPassword` / `fields.password`; 423 `reauth_required` bij instellen zonder recente login. */
    changePassword: ep<PasswordChangeRequest, void>()({ method: 'POST', path: () => '/account/password' }),
    exportData: ep<void, AccountExport>()({ method: 'GET', path: () => '/account/export' }),
    /** 423 `reauth_required`: eerst opnieuw inloggen. */
    deleteAccount: ep<AccountDeleteRequest, void>()({ method: 'DELETE', path: () => '/account' }),
    /** Alleen met nieuwsbrieftoestemming; anders bewaart de server niets. Voor de mail "verlaten winkelmand". */
    cartSync: ep<CartSyncRequest, void>()({ method: 'PUT', path: () => '/account/cart' }),
  },

  /** Beheer — beheersessie, rol admin. */
  admin: {
    /** 401 = niet ingelogd. */
    me: ep<void, AdminUser>()({ method: 'GET', path: () => '/admin/me' }),
    /** `{ status: 'two_factor' }` = wachtwoord klopt, nu de code (geen sessie tot die klopt). */
    login: ep<AdminLoginRequest, AdminLoginResponse>()({ method: 'POST', path: () => '/admin/login' }),
    logout: ep<void, void>()({ method: 'POST', path: () => '/admin/logout' }),
    /** 422 = code klopt niet; 429 = te vaak geprobeerd. */
    twoFactorChallenge: ep<TwoFactorChallengeRequest, AdminUser>()({ method: 'POST', path: () => '/admin/two-factor-challenge' }),
    /** Nieuwe sleutel (nog niet actief tot confirm). */
    twoFactorSetup: ep<void, TwoFactorSetup>()({ method: 'POST', path: () => '/admin/two-factor', parse: parseTwoFactorSetup }),
    twoFactorConfirm: ep<TwoFactorConfirmRequest, TwoFactorConfirmResponse>()({
      method: 'POST',
      path: () => '/admin/two-factor/confirm',
    }),
    recoveryCodes: ep<void, RecoveryCodesResponse>()({ method: 'POST', path: () => '/admin/two-factor/recovery-codes' }),
    /** 404 = nog nooit opgeslagen. */
    content: ep<void, AdminContent>()({ method: 'GET', path: () => '/admin/content' }),
    /** 409 `version_conflict` als `revision` niet de opgeslagen versie is. Negeert `stock` van bestaande producten. */
    saveContent: ep<AdminContent, AdminContent>()({ method: 'PUT', path: () => '/admin/content' }),
    /** Voorraad meteen opslaan, met de verwachte stand (`applied: false` als er intussen iets verkocht is). */
    productStock: ep<StockUpdateRequest, StockUpdateResponse>()({
      method: 'PATCH',
      path: (id: string) => `/admin/products/${enc(id)}/stock`,
    }),
    customers: ep<void, CustomerSummary[]>()({ method: 'GET', path: () => '/admin/customers' }),
    /**
     * Alle klanten voor de CSV (BEHEER-PLAN §3.6). Alleen de eigenaar; vraagt een recente login
     * (423 `reauth_required`) en legt vast wie wanneer exporteerde. Dezelfde velden als de lijst.
     */
    customersExport: ep<void, CustomerSummary[]>()({ method: 'POST', path: () => '/admin/customers/export' }),
    /** Klant aanmaken (CustomerCreateRequest); 409 `duplicate` als het e-mailadres al bestaat. */
    customerCreate: ep<CustomerCreateRequest, { id: string }>()({ method: 'POST', path: () => '/admin/customers' }),
    /** `sourceId` gaat op in `:id`. 409 `two_logins` | `two_subscriptions` | `two_courses`. */
    customerMerge: ep<CustomerMergeRequest, CustomerFile>()({ method: 'POST', path: (id: string) => `${customer(id)}/merge` }),
    /** Naam en telefoon; e-mail alleen zonder login. 409 `duplicate` | `email_locked`. */
    customerContact: ep<CustomerContactRequest, Customer>()({ method: 'PATCH', path: (id: string) => `${customer(id)}/contact` }),
    /** 404 = bestaat niet. */
    customer: ep<void, CustomerFile>()({ method: 'GET', path: (id: string) => customer(id) }),
    plan: ep<SkinPlan, SkinPlan>()({ method: 'PUT', path: (id: string) => `${customer(id)}/plan` }),
    course: ep<Course | null, Course | null>()({ method: 'PATCH', path: (id: string) => `${customer(id)}/course` }),
    /**
     * Een vooruitbetaalde kuur stoppen en terugbetalen (creditnota). Idempotent op `requestId`.
     * 409 `not_prepaid` | `day_closed` (pin/contant na de dagafsluiting); 422 bij een bedrag of reden die niet klopt.
     */
    kuurStop: ep<KuurStopRequest, KuurStop>()({ method: 'POST', path: (id: string) => `${customer(id)}/course/stop` }),
    note: ep<NoteRequest, NoteRequest>()({ method: 'PATCH', path: (id: string) => `${customer(id)}/note` }),
    /** Niet online laten boeken (aan met reden, of uit). 422 zonder reden. */
    bookingBlock: ep<BookingBlockRequest, BookingBlockResponse>()({ method: 'PUT', path: (id: string) => `${customer(id)}/booking-block` }),
    addAppointment: ep<NewAppointmentRequest, Appointment>()({
      method: 'POST',
      path: (id: string) => `${customer(id)}/appointments`,
    }),
    confirmAppointment: ep<void, Appointment>()({
      method: 'POST',
      path: (id: string, aid: string) => `${appointment(id, aid)}/confirm`,
    }),
    proposeAppointment: ep<ProposeRequest, Appointment>()({
      method: 'POST',
      path: (id: string, aid: string) => `${appointment(id, aid)}/propose`,
    }),
    completeAppointment: ep<void, CompleteResponse>()({
      method: 'POST',
      path: (id: string, aid: string) => `${appointment(id, aid)}/complete`,
    }),
    removeAppointment: ep<void, void>()({ method: 'DELETE', path: (id: string, aid: string) => appointment(id, aid) }),
    subscriptionAction: ep<SubscriptionActionRequest, Subscription | null>()({
      method: 'POST',
      path: (id: string) => `${customer(id)}/subscription/actions`,
    }),
    diaryMark: ep<DiaryMarkRequest, DiaryEntry>()({
      method: 'PATCH',
      path: (id: string, entryId: string) => `${customer(id)}/diary/${enc(entryId)}`,
    }),
    inbox: ep<void, Inbox>()({ method: 'GET', path: () => '/admin/inbox' }),
    orderStatus: ep<OrderStatusRequest, InboxOrder>()({ method: 'PATCH', path: (id: string) => `/admin/orders/${enc(id)}` }),
    confirmGuestBooking: ep<void, InboxBooking>()({
      method: 'POST',
      path: (id: string) => `/admin/bookings/${enc(id)}/confirm`,
    }),

    /** Agenda: alles tussen twee datums (inclusief), ook afgelopen afspraken met hun afloop. */
    agenda: ep<void, AgendaRange>()({
      method: 'GET',
      path: (from: string, to: string) => `/admin/agenda?from=${enc(from)}&to=${enc(to)}`,
    }),
    /** Idempotent op `requestId`. 409 `slot_taken` bij overlap, tenzij `allowOverlap`. */
    agendaAdd: ep<AgendaNewRequest, AgendaAppointment>()({ method: 'POST', path: () => '/admin/agenda/appointments' }),
    /** Herhaalafspraken: elke datum los gecontroleerd; wat niet lukt staat in `skipped`. Alleen de eigenaar. */
    agendaSeries: ep<AgendaSeriesRequest, AgendaSeriesResult>()({ method: 'POST', path: () => '/admin/agenda/series' }),
    /** Verzetten; de klant krijgt bericht met een nieuwe agenda-uitnodiging. 409 `slot_taken` zoals hierboven. */
    agendaMove: ep<AgendaMoveRequest, AgendaAppointment>()({ method: 'PATCH', path: (id: string) => agendaItem(id) }),
    /** Een collega alleen voor een aanvraag bij haar, op haar eigen salondag (403 `not_your_request`). Legt `handledBy` vast. */
    agendaConfirm: ep<void, AgendaAppointment>()({ method: 'POST', path: (id: string) => `${agendaItem(id)}/confirm` }),
    /** "Binnen": de klant is er. Alleen op de dag zelf (422); een collega alleen bij haar eigen afspraak (403). Idempotent. */
    agendaArrive: ep<void, AgendaAppointment>()({ method: 'POST', path: (id: string) => `${agendaItem(id)}/arrived` }),
    /** Wordt een bezoek bij de klant (met het advies), telt mee in de kuur. 409 als hij al afgerond is. */
    agendaComplete: ep<AgendaCompleteRequest, AgendaAppointment>()({ method: 'POST', path: (id: string) => `${agendaItem(id)}/complete` }),
    /** Pas vanaf de begintijd. */
    agendaNoShow: ep<void, AgendaAppointment>()({ method: 'POST', path: (id: string) => `${agendaItem(id)}/no-show` }),
    /** Annuleren door de salon: blijft zichtbaar als geannuleerd, de klant krijgt bericht. */
    agendaCancel: ep<void, AgendaAppointment>()({ method: 'POST', path: (id: string) => `${agendaItem(id)}/cancel` }),
    agendaAddBlock: ep<AgendaBlockRequest, AgendaBlock>()({ method: 'POST', path: () => '/admin/agenda/blocks' }),
    agendaRemoveBlock: ep<void, void>()({ method: 'DELETE', path: (id: string) => `/admin/agenda/blocks/${enc(id)}` }),
    /**
     * Afwezig op één dag (alleen de eigenaar): legt de afwijkende dag vast en zet de open afspraken
     * naar Sarah, of annuleert ze en mailt de klanten ("Afspraak gaat niet door" met verzetlink).
     * Idempotent op `requestId`; 409 `not_working`.
     */
    agendaAbsence: ep<AgendaAbsenceRequest, AgendaAbsenceResult>()({ method: 'POST', path: () => '/admin/agenda/absence' }),
    /** Geheime ICS-link (alleen lezen) voor Google/Apple Agenda. Per medewerker: alleen haar afspraken. */
    agendaFeed: ep<void, AgendaFeed>()({ method: 'GET', path: () => '/admin/agenda/feed' }),

    /** Medewerkers: de logins bij de profielen in de instellingen. Alleen de eigenaar. */
    staffLogins: ep<void, StaffLogin[]>()({ method: 'GET', path: () => '/admin/staff/logins' }),
    /** Uitnodiging per mail; de collega kiest zelf een wachtwoord en moet 2FA instellen. 409 `email_taken`. */
    staffInvite: ep<StaffInviteRequest, StaffLogin>()({ method: 'POST', path: (id: string) => `/admin/staff/${enc(id)}/invite` }),
    /** Login intrekken: sessies vervallen meteen. Recente 2FA (423 `reauth_required`). */
    staffRevoke: ep<void, void>()({ method: 'DELETE', path: (id: string) => `/admin/staff/${enc(id)}/login` }),
    /** Rol en einddatum van een login. 409 `last_owner`, 423 `reauth_required`. */
    staffAccess: ep<StaffAccessRequest, StaffLogin>()({ method: 'PATCH', path: (id: string) => `/admin/team/accounts/${enc(id)}` }),
    /**
     * Stoppen in één transactie: persoon inactief (`site.staff[].active = false`), login ingetrokken,
     * alle sessies weg. 409 `last_owner`, 423 `reauth_required`. Idempotent.
     */
    staffStop: ep<void, void>()({ method: 'POST', path: (id: string) => `/admin/team/accounts/${enc(id)}/stop` }),
    /** Alle sessies van dit account beëindigen; de login blijft. 423 `reauth_required`. */
    staffSessionsRevoke: ep<void, StaffLogin>()({ method: 'DELETE', path: (id: string) => `/admin/team/accounts/${enc(id)}/sessions` }),
    /** Opnieuw bevestigen wie je bent (wachtwoord, en de code als 2FA aan staat). 15 minuten geldig. 422 = klopt niet. */
    reauth: ep<ReauthRequest, void>()({ method: 'POST', path: () => '/admin/reauth' }),
    /** Het logboek (alleen-toevoegen), nieuwste eerst. Alleen `audit.view`. */
    audit: ep<void, AuditPage>()({ method: 'GET', path: (query: AuditQuery) => `/admin/audit${queryString(query)}` }),
    /**
     * De beperkte klantkaart voor een collega: alleen als de klant een afspraak bij haar heeft (anders
     * 403). Naam, telefoon, huidplan en eerdere behandelingen met notities; geen bestellingen, tegoed,
     * abonnement, foto's of de interne notitie van Sarah.
     */
    staffCustomer: ep<void, StaffCustomerCard>()({ method: 'GET', path: (id: string) => `/admin/staff/customers/${enc(id)}` }),
    /** Ongelezen overdrachtsnotities (alleen de eigenaar), nieuwste eerst. */
    handoffNotes: ep<void, HandoffNote[]>()({ method: 'GET', path: () => '/admin/handoff-notes?status=ongelezen' }),
    /** Notitie aan Sarah bij een klant. Een collega alleen bij een klant met een afspraak bij haar (403). */
    handoffNoteAdd: ep<HandoffNoteRequest, HandoffNote>()({ method: 'POST', path: (customerId: string) => `/admin/customers/${enc(customerId)}/handoff-notes` }),
    /** Als gelezen markeren (alleen de eigenaar). Idempotent. */
    handoffNoteRead: ep<void, HandoffNote>()({ method: 'POST', path: (id: string) => `/admin/handoff-notes/${enc(id)}/read` }),

    /** Bestellingen: alle, ook bezorgd en geannuleerd. Nieuwste bovenaan, per pagina. */
    orders: ep<void, AdminOrderPage>()({ method: 'GET', path: (query: AdminOrderQuery) => `/admin/orders${queryString(query)}` }),
    order: ep<void, AdminOrder>()({ method: 'GET', path: (id: string) => `/admin/orders/${enc(id)}` }),
    /** Via Mollie. Idempotent op `requestId`. 409 `already_refunded`, 422 als niet betaald. */
    orderRefund: ep<RefundRequest, AdminOrder>()({ method: 'POST', path: (id: string) => `/admin/orders/${enc(id)}/refund` }),
    /** Alleen onbetaald, of betaald en nog 'nieuw' (dan ook terugbetalen). 409 `cannot_cancel`. Voorraad terug. */
    orderCancel: ep<CancelOrderRequest, AdminOrder>()({ method: 'POST', path: (id: string) => `/admin/orders/${enc(id)}/cancel` }),

    /** Omzet, door de server berekend volgens core/rules/revenue.ts. */
    salesReport: ep<void, SalesReport>()({
      method: 'GET',
      path: (from: string, to: string, groupBy: SalesGroupBy, channel?: SalesChannel) => `/admin/reports/sales${queryString({ from, to, groupBy, channel })}`,
    }),
    /**
     * Inzicht (docs/INZICHT.md). Eigen data rekent de server live; externe bronnen komen uit de
     * dagregels die hij elke nacht ophaalt. Een externe bron zonder koppeling: 404 `not_connected`.
     */
    insightsOverview: ep<void, InsightsOverview>()({ method: 'GET', path: (from: string, to: string) => `/admin/insights/overview${queryString({ from, to })}` }),
    insightsCustomers: ep<void, CustomerInsights>()({ method: 'GET', path: (from: string, to: string) => `/admin/insights/customers${queryString({ from, to })}` }),
    insightsSalon: ep<void, SalonInsights>()({ method: 'GET', path: (from: string, to: string) => `/admin/insights/salon${queryString({ from, to })}` }),
    insightsWebshop: ep<void, WebshopInsights>()({ method: 'GET', path: (from: string, to: string) => `/admin/insights/webshop${queryString({ from, to })}` }),
    insightsSources: ep<void, SourceInsights>()({ method: 'GET', path: (from: string, to: string) => `/admin/insights/sources${queryString({ from, to })}` }),
    insightsVisitors: ep<void, VisitorsReport>()({ method: 'GET', path: (from: string, to: string) => `/admin/insights/visitors${queryString({ from, to })}` }),
    insightsSearch: ep<void, SearchReport>()({ method: 'GET', path: (from: string, to: string) => `/admin/insights/search${queryString({ from, to })}` }),
    insightsBusiness: ep<void, BusinessProfileReport>()({ method: 'GET', path: (from: string, to: string) => `/admin/insights/business${queryString({ from, to })}` }),
    insightsSocial: ep<void, SocialReport>()({ method: 'GET', path: (from: string, to: string) => `/admin/insights/social${queryString({ from, to })}` }),
    insightsMail: ep<void, MailInsights>()({ method: 'GET', path: (from: string, to: string) => `/admin/insights/mail${queryString({ from, to })}` }),
    integrations: ep<void, Integrations>()({ method: 'GET', path: () => '/admin/integrations' }),
    /** 202: ophalen staat in de wachtrij. Hoogstens 1× per 10 minuten per bron (429). */
    integrationRefresh: ep<void, IntegrationStatus>()({ method: 'POST', path: (id: IntegrationId) => `/admin/integrations/${enc(id)}/refresh` }),
    /** Voorraad per plek (docs/VOORRAAD.md). Alleen beheer: inkoopprijs en salonvoorraad staan nooit in GET /catalog. */
    stock: ep<void, StockOverview>()({ method: 'GET', path: () => '/admin/stock' }),
    stockMovements: ep<void, StockMovement[]>()({ method: 'GET', path: (itemId: string) => `/admin/stock/items/${enc(itemId)}/movements` }),
    /** Idempotent op `requestId`. 422 bij een aantal dat niet kan; 409 `stock_changed` bij een telling op een verouderde stand. */
    stockMovementAdd: ep<StockMovementRequest, StockItem>()({ method: 'POST', path: () => '/admin/stock/movements' }),
    stockItemAdd: ep<NewStockItemRequest, StockItem>()({ method: 'POST', path: () => '/admin/stock/items' }),
    stockItemPatch: ep<StockItemPatch, StockItem>()({ method: 'PATCH', path: (id: string) => `/admin/stock/items/${enc(id)}` }),
    /** Geboekte inkoopfacturen, nieuwste eerst; `file.url` is een tijdelijke, getekende link. */
    stockReceipts: ep<void, PurchaseReceipt[]>()({ method: 'GET', path: () => '/admin/stock/receipts' }),
    /** Multipart (`file`, `lines` als JSON). Idempotent op `requestId`; 409 `receipt_duplicate` bij een al geboekte factuur. */
    stockReceiptAdd: ep<PurchaseReceiptRequest, PurchaseReceipt>()({ method: 'POST', path: () => '/admin/stock/receipts' }),
    stockCounts: ep<void, StockCount[]>()({ method: 'GET', path: () => '/admin/stock/counts' }),
    /** Idempotent op `requestId`. Regels waarvan de stand intussen veranderde, komen terug in `conflicts` (niet geboekt). */
    stockCountAdd: ep<StockCountRequest, StockCountResult>()({ method: 'POST', path: () => '/admin/stock/counts' }),
    stockMovementsExport: ep<void, string>()({
      method: 'GET',
      path: (from: string, to: string) => `/admin/reports/stock-movements.csv${queryString({ from, to })}`,
      format: 'text',
    }),
    /** Kassa: bonnen, afspraken zonder bon en de afsluiting van één dag (core/rules/kassa.ts). */
    kassaDay: ep<void, KassaDay>()({ method: 'GET', path: (day: string) => `/admin/kassa/days/${enc(day)}` }),
    /** Idempotent op `requestId`. 409 `day_closed` | `already_paid` | `kuur_conflict`; 422 bij een regel, bon of contant bedrag dat niet klopt. */
    saleCreate: ep<SaleRequest, Sale>()({ method: 'POST', path: () => '/admin/kassa/sales' }),
    /** Alleen op de dag zelf en vóór afsluiten (409 `too_late`); niet met een kuur erop (409 `kuur_in_sale`). */
    saleVoid: ep<VoidSaleRequest, Sale>()({ method: 'POST', path: (id: string) => `/admin/kassa/sales/${enc(id)}/void` }),
    /**
     * Retour op een bon, op elke dag (core/rules/saleReturn.ts). Alleen de eigenaar (403). Idempotent
     * op `requestId`. 409 `voided` | `day_closed` (pin/contant na de afsluiting van vandaag); 422 bij regels, methode of reden.
     */
    saleReturn: ep<SaleReturnRequest, SaleReturn>()({ method: 'POST', path: (id: string) => `/admin/kassa/sales/${enc(id)}/returns` }),
    /** Idempotent op `requestId`; 409 `day_closed` als hij al afgesloten is. Bij een kasverschil is `note` verplicht (422). */
    kassaClose: ep<CloseDayRequest, KassaDay>()({ method: 'POST', path: (day: string) => `/admin/kassa/days/${enc(day)}/close` }),
    /** Alle dagafsluitingen in een periode, nieuwste eerst, met wie afsloot. Alleen de eigenaar (403). */
    kassaCloses: ep<void, CashDayClose[]>()({ method: 'GET', path: (from: string, to: string) => `/admin/kassa/closes${queryString({ from, to })}` }),
    /** Cadeaubonnen: nieuwste bovenaan, per pagina. Zoeken op code (heel of laatste vier) of koper. */
    vouchers: ep<void, VoucherPage>()({ method: 'GET', path: (query: VoucherQuery) => `/admin/vouchers${queryString(query)}` }),
    /** Idempotent op `requestId`. De server maakt de code (cryptografisch willekeurig). */
    voucherCreate: ep<NewVoucherRequest, Voucher>()({ method: 'POST', path: () => '/admin/vouchers' }),
    /** Op id, niet op code: de code hoort niet in een URL. Met de volledige code (inzien wordt gelogd). */
    voucher: ep<void, Voucher>()({ method: 'GET', path: (id: string) => `/admin/vouchers/${enc(id)}` }),
    /** Correctieregel met reden. Idempotent op `requestId`; 422 onder 0 of op een verlopen bon. */
    voucherCorrect: ep<VoucherCorrectionRequest, Voucher>()({ method: 'POST', path: (id: string) => `/admin/vouchers/${enc(id)}/corrections` }),
    /** Blokkeren (reden verplicht) of opheffen. Idempotent op `requestId`. */
    voucherBlock: ep<VoucherBlockRequest, Voucher>()({ method: 'POST', path: (id: string) => `/admin/vouchers/${enc(id)}/block` }),
    /** Vooruitbetaald en openstaand op het eind van een dag (core/rules/liabilities.ts). */
    liabilitiesReport: ep<void, LiabilitiesReport>()({ method: 'GET', path: (on: string) => `/admin/reports/liabilities${queryString({ on })}` }),
    /** Tegoed van een klant, per eenheid. */
    customerCredit: ep<void, CustomerCredit>()({ method: 'GET', path: (id: string) => `${customer(id)}/credit` }),
    /** Correctie met reden. Idempotent op `requestId`; 422 als het saldo onder 0 zou komen. */
    customerCreditCorrect: ep<CreditCorrectionRequest, CustomerCredit>()({ method: 'POST', path: (id: string) => `${customer(id)}/credit` }),

    /** Doorverwijzingen (301) en de stand van de sitemap. */
    redirects: ep<void, RedirectsResponse>()({ method: 'GET', path: () => '/admin/redirects' }),
    /** De hele lijst. 409 `version_conflict`; 422 bij een lus, dubbel of ongeldig pad (core/rules/redirects.ts). */
    redirectsSave: ep<RedirectsRequest, RedirectsResponse>()({ method: 'PUT', path: () => '/admin/redirects' }),

    /** Automatische mails, reeksen en de huisstijl van alle mails. */
    mails: ep<void, MailSettings>()({ method: 'GET', path: () => '/admin/mails' }),
    /** Alles in één keer. 409 `version_conflict`; 422 als een verplichte mail uit staat of een veld onbekend is. */
    mailsSave: ep<MailSettings, MailSettings>()({ method: 'PUT', path: () => '/admin/mails' }),
    /** Testmail naar wie is ingelogd, met de voorbeeldwaarden. Idempotent op `requestId`. */
    mailTest: ep<MailTestRequest, OkResponse>()({ method: 'POST', path: (key: string) => `/admin/mails/${enc(key)}/test` }),

    /**
     * Nieuwsbrief versturen. De inhoud is `marketing.newsletters[]` uit PUT /admin/content (zelfde id);
     * de server verstuurt altijd de opgeslagen versie. Zie core/types/mail.ts voor de foutcodes.
     */
    newsletterSend: ep<NewsletterSendRequest, NewsletterDeliveryResponse>()({ method: 'POST', path: (id: string) => `/admin/newsletters/${enc(id)}/send` }),
    newsletterSchedule: ep<NewsletterScheduleRequest, NewsletterDeliveryResponse>()({
      method: 'POST',
      path: (id: string) => `/admin/newsletters/${enc(id)}/schedule`,
    }),
    newsletterTest: ep<MailTestRequest, OkResponse>()({ method: 'POST', path: (id: string) => `/admin/newsletters/${enc(id)}/test` }),

    /** Abonnees met groei per week. Zoeken op e-mail of naam. */
    subscribers: ep<void, SubscriberPage>()({ method: 'GET', path: (query: SubscriberQuery) => `/admin/subscribers${queryString(query)}` }),
    /** Doelgroepen met het aantal bevestigde abonnees per groep (door de server geteld). */
    segments: ep<void, SegmentList>()({ method: 'GET', path: () => '/admin/segments' }),
    segmentsSave: ep<SegmentsRequest, SegmentList>()({ method: 'PUT', path: () => '/admin/segments' }),

    /** CSV per dag voor de boekhouder (Exact Online). Platte tekst, geen JSON. */
    salesExport: ep<void, string>()({
      method: 'GET',
      path: (from: string, to: string) => `/admin/reports/export.csv${queryString({ from, to })}`,
      format: 'text',
    }),
  },
}

/** `{ q: 'eva', page: 2 }` → "?q=eva&page=2". Lege waarden vallen weg. */
function queryString(params: Record<string, string | number | undefined>): string {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${enc(k)}=${enc(String(v))}`)
  return parts.length ? `?${parts.join('&')}` : ''
}
